import React, { useState, useEffect } from 'react';
import { DocumentService, EmbeddingStatsResponse } from '../../utils/apiService';

interface EmbeddingProgressBarProps {
  refreshInterval?: number; // Refresh interval in ms, default 0 (disabled)
  onEmbed?: () => void;     // Callback when user clicks the embed button
  autoRefresh?: boolean;    // Whether to auto-refresh, default false
}

const EmbeddingProgressBar: React.FC<EmbeddingProgressBarProps> = ({ 
  refreshInterval = 0,  // Disabled by default to prevent excessive calls
  onEmbed,
  autoRefresh = false  // Disabled by default
}) => {
  const [stats, setStats] = useState<EmbeddingStatsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await DocumentService.getEmbeddingStats();
      setStats(response);
      setError(null);
    } catch (err: any) {
      console.error("Error fetching embedding stats:", err);
      
      // Better error handling
      let errorMessage = "Failed to fetch embedding statistics";
      
      if (err.message) {
        if (err.message.includes('authentication') || err.message.includes('401')) {
          errorMessage = "Autenticazione richiesta. Effettua il login.";
        } else if (err.message.includes('configuration')) {
          errorMessage = "Configurazione mancante. Imposta una configurazione database.";
        } else if (err.message.includes('500') || err.message.includes('Internal Server Error')) {
          errorMessage = "Errore del server. Riprova più tardi.";
        } else {
          errorMessage = err.message;
        }
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch on mount
  useEffect(() => {
    fetchStats();
  }, []);

  // Set up the refresh interval only if autoRefresh is enabled and refreshInterval > 0
  useEffect(() => {
    if (autoRefresh && refreshInterval > 0) {
      const intervalId = setInterval(() => {
        fetchStats();
      }, refreshInterval);

      return () => clearInterval(intervalId);
    }
  }, [refreshInterval, autoRefresh]);

  // If loading and no previous stats, show a skeleton loader
  if (loading && !stats) {
    return (
      <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mb-4 animate-pulse">
        <div className="h-4 w-32 bg-gray-200 dark:bg-gray-600 rounded mb-2"></div>
        <div className="h-6 w-full bg-gray-200 dark:bg-gray-600 rounded"></div>
      </div>
    );
  }

  // If error and it's an authentication error, don't show the component to prevent spam
  if (error && error.includes('Authentication required')) {
    return null;
  }

  // If error, show error message
  if (error && !stats) {
    return (
      <div className="w-full bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
        <p className="text-red-600 dark:text-red-400 text-sm">Errore: {error}</p>
        <button 
          onClick={fetchStats}
          className="mt-2 text-xs bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-300 px-2 py-1 rounded"
        >
          Riprova
        </button>
      </div>
    );
  }

  // If no stats, just return null (or a placeholder)
  if (!stats) return null;

  // Calculate color based on percentage
  const getColorClass = (percentage: number) => {
    if (percentage < 30) return "bg-red-500";
    if (percentage < 70) return "bg-yellow-500";
    return "bg-green-500";
  };

  const progressColor = getColorClass(stats.embedding_percentage);
  const needsAttention = stats.embedding_percentage < 100;
  
  return (
    <div className="w-full bg-white dark:bg-gray-800 rounded-lg p-4 mb-4 shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Indicizzazione
        </h3>
        
        <div className="flex items-center space-x-2">
          {loading && (
            <span className="text-xs text-gray-500 dark:text-gray-400 animate-pulse">
              Aggiornamento...
            </span>
          )}
          <button 
            onClick={fetchStats}
            className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>
      
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 mb-3">
        <div 
          className={`${progressColor} h-4 rounded-full transition-all duration-500 ease-in-out`}
          style={{ width: `${Math.min(100, Math.max(0, stats.embedding_percentage))}%` }}
        ></div>
      </div>
      
      <div className="flex justify-between items-center text-xs text-gray-600 dark:text-gray-400">
        <span>
          {stats.embedded_documents} di {stats.total_documents} documenti indicizzati
        </span>
        <span className="font-medium">
          {Math.round(Math.min(100, Math.max(0, stats.embedding_percentage)))}%
        </span>
      </div>
      
      {needsAttention && onEmbed && (
        <div className="mt-3 flex justify-end">
          <button
            onClick={onEmbed}
            className="text-xs bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300 px-3 py-1.5 rounded-md hover:bg-blue-200 dark:hover:bg-blue-700 flex items-center transition-colors"
          >
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Indicizza Documenti non indicizzati
          </button>
        </div>
      )}
    </div>
  );
};

export default EmbeddingProgressBar; 